import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
  makeStyles
} from '@material-ui/core'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import PropTypes from 'prop-types'
import React from 'react'

import { descendantIds, isIndeterminate } from 'app/permissions'
import { colors } from 'app/theme/colors'

const useStyles = makeStyles((theme) => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 32
  },
  toggle: {
    padding: 2,
    marginRight: 2
  },
  togglePlaceholder: {
    width: 24,
    marginRight: 2,
    flexShrink: 0
  },
  label: {
    margin: 0
  },
  sectionLabel: {
    fontWeight: 600
  },
  count: {
    marginLeft: theme.spacing(1),
    color: colors.textTertiary,
    fontSize: 12
  },
  children: {
    marginLeft: theme.spacing(3),
    borderLeft: `1px solid ${colors.borderDefault}`,
    paddingLeft: theme.spacing(1)
  }
}))

const PermissionNode = ({
  node,
  depth,
  selectedIds,
  expandedIds,
  disabled,
  onToggle,
  onExpand
}) => {
  const classes = useStyles()
  const hasChildren = node.children.length > 0
  const isExpanded = expandedIds.has(node.id)
  const checked = selectedIds.has(node.id)
  const partial = isIndeterminate(node, selectedIds)

  const subtree = React.useMemo(() => descendantIds(node), [node])
  const selectedInSubtree = subtree.filter((id) => selectedIds.has(id)).length

  return (
    <Box>
      <Box className={classes.row}>
        {hasChildren ? (
          <IconButton
            size="small"
            className={classes.toggle}
            onClick={() => onExpand(node.id, !isExpanded)}
          >
            {isExpanded ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </IconButton>
        ) : (
          <span className={classes.togglePlaceholder} />
        )}
        <FormControlLabel
          className={classes.label}
          control={
            <Checkbox
              size="small"
              color="primary"
              checked={checked}
              indeterminate={partial}
              disabled={disabled}
              onChange={(event) => onToggle(node.id, event.target.checked)}
            />
          }
          label={
            <Typography
              variant="body2"
              className={depth === 0 ? classes.sectionLabel : undefined}
            >
              {node.label}
            </Typography>
          }
        />
        {hasChildren && (
          <span className={classes.count}>
            {selectedInSubtree}/{subtree.length}
          </span>
        )}
      </Box>

      {hasChildren && isExpanded && (
        <Box className={classes.children}>
          {node.children.map((child) => (
            <PermissionNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedIds={selectedIds}
              expandedIds={expandedIds}
              disabled={disabled}
              onToggle={onToggle}
              onExpand={onExpand}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

PermissionNode.propTypes = {
  node: PropTypes.object.isRequired,
  depth: PropTypes.number.isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  expandedIds: PropTypes.instanceOf(Set).isRequired,
  disabled: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onExpand: PropTypes.func.isRequired
}

const PermissionTree = ({
  nodes,
  selectedIds,
  expandedIds,
  disabled,
  onToggle,
  onExpand
}) => (
  <Box>
    {nodes.map((node) => (
      <PermissionNode
        key={node.id}
        node={node}
        depth={0}
        selectedIds={selectedIds}
        expandedIds={expandedIds}
        disabled={disabled}
        onToggle={onToggle}
        onExpand={onExpand}
      />
    ))}
  </Box>
)

PermissionTree.propTypes = {
  nodes: PropTypes.array.isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  expandedIds: PropTypes.instanceOf(Set).isRequired,
  // Super Admin's tree renders fully ticked but uneditable.
  disabled: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onExpand: PropTypes.func.isRequired
}

PermissionTree.defaultProps = { disabled: false }
PermissionNode.defaultProps = { disabled: false }

export default PermissionTree
